import { isValidElement, ReactElement } from 'react';
import toast from 'react-hot-toast';
import { twJoin } from 'tailwind-merge';

import { CellContext, Icon, RyeconCopy, Sizes, Tooltip } from '@dbt-labs/sourdough';

type TruncatedCellProps<TData extends object> = CellContext<TData, ReactElement>;

const TruncatedCellImpl = ({
  className,
  innerClassName,
  value,
  tooltipContent,
  onClick,
}: {
  className?: string;
  innerClassName?: string;
  value: ReactElement | string;
  tooltipContent?: ReactElement | string;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
}) => {
  const content =
    tooltipContent || isValidElement(value) ? value : value?.toLocaleString();
  return (
    <Tooltip
      displayOnlyWhenTruncated
      content={content}
      className={twJoin(className, 'w-full')}
      childrenAreInteractive={true}
      placement="top-end" // most of the time this is the last cell in the row
    >
      {(ref) => (
        <div ref={ref} className={twJoin('truncate', innerClassName)} onClick={onClick}>
          {value}
        </div>
      )}
    </Tooltip>
  );
};

/**
 * A cell that is truncated and displays a tooltip of the full content on hover.
 */
export const TruncatedCell = <TData extends object>({
  getValue,
}: TruncatedCellProps<TData>) => {
  const value = getValue();
  return <TruncatedCellImpl value={value} />;
};

/**
 * A cell that is truncated and displays a tooltip of the full content on hover.
 * Should set meta.align="right" to right align the header.
 */
export const RightAlignedTruncatedCell = <TData extends object>({
  getValue,
}: TruncatedCellProps<TData>) => {
  const value = getValue();
  return <TruncatedCellImpl value={value} className="text-right" />;
};

type TruncatedCopyLinkCellProps<TData extends object> = CellContext<TData, string>;

/**
 * A cell that is truncated and displays a tooltip of the full content on hover.
 * Uses link styling. When clicked, copies content to clipboard
 */
export const TruncatedCopyLinkCell = <TData extends object>({
  getValue,
}: TruncatedCopyLinkCellProps<TData>) => {
  const value = getValue();
  return (
    <TruncatedCellImpl
      innerClassName="py-4 cursor-pointer"
      value={
        <span className="whitespace-nowrap py-4">
          <Icon className="mr-1 align-middle" ryecon={RyeconCopy} size={Sizes.sm} />
          <span className="align-middle">{value}</span>
        </span>
      }
      tooltipContent={value}
      onClick={(e) => {
        e.preventDefault();
        navigator.clipboard.writeText(value);
        toast.success('Copied to clipboard', {
          id: 'copy-to-clipboard',
        });
      }}
    />
  );
};
